import {
  CheckCircle,
  Copy,
  Download,
  ExternalLink,
  Eye,
  MoreHorizontal,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'
import type { Signal } from '../types/events.types'

interface SignalActionMenuProps {
  signal: Signal
  onViewDetails: (signal: Signal) => void
  className?: string
}

export function SignalActionMenu({
  signal,
  onViewDetails,
  className,
}: SignalActionMenuProps) {
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false)
  const [showRawData, setShowRawData] = useState(false)

  const copyLinkToEvent = async () => {
    const eventUrl = `${window.location.origin}/developers/signals?event=${signal.id}&timestamp=${signal.timestamp}`

    try {
      await navigator.clipboard.writeText(eventUrl)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    } catch (err) {
      toast.error(extractErrorMessage(err))
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = eventUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    }
  }

  const copyEventData = async () => {
    const eventData = JSON.stringify(
      {
        id: signal.id,
        event: signal.signal_name,
        properties: signal.properties,
        data: signal.data,
        timestamp: signal.timestamp,
        person: signal.person,
        library: signal.library,
      },
      null,
      2
    )

    try {
      await navigator.clipboard.writeText(eventData)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    } catch (err) {
      toast.error(extractErrorMessage(err))
    }
  }

  const exportSingleEvent = () => {
    const eventData = {
      id: signal.id,
      event: signal.signal_name,
      properties: signal.properties,
      data: signal.data,
      timestamp: signal.timestamp,
      person: signal.person,
      library: signal.library,
      library_version: signal.library_version,
      url: signal.url,
      screen: signal.screen,
    }

    const dataStr = JSON.stringify(eventData, null, 2)
    const dataUri =
      'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)

    const exportFileDefaultName = `signal_${signal.id}_${new Date().toISOString().slice(0, 10)}.json`

    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className={cn('h-8 w-8 p-0 data-[state=open]:bg-muted', className)}
          >
            <MoreHorizontal className='h-4 w-4' />
            <span className='sr-only'>Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-48'>
          <DropdownMenuItem onClick={() => onViewDetails(signal)}>
            <Eye className='mr-2 h-4 w-4' />
            View details
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={copyLinkToEvent}>
            <div className='flex items-center'>
              {showCopiedFeedback ? (
                <CheckCircle className='mr-2 h-4 w-4 text-green-600' />
              ) : (
                <ExternalLink className='mr-2 h-4 w-4' />
              )}
              {showCopiedFeedback ? 'Link copied!' : 'Copy link to event'}
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={copyEventData}>
            <Copy className='mr-2 h-4 w-4' />
            Copy event data
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setShowRawData(true)}>
            <Eye className='mr-2 h-4 w-4' />
            View raw data
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={exportSingleEvent}>
            <Download className='mr-2 h-4 w-4' />
            Export as JSON
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Raw Data Modal */}
      <Dialog open={showRawData} onOpenChange={setShowRawData}>
        <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Eye className='w-5 h-5' />
              Raw Event Data
            </DialogTitle>
            <DialogDescription>
              {signal.signal_name} • {signal.id} •{' '}
              {new Date(signal.timestamp).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <h4 className='text-sm font-medium'>Full Event Object</h4>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={copyEventData}
                  className='text-xs'
                >
                  <Copy className='w-3 h-3 mr-1' />
                  Copy JSON
                </Button>
              </div>
              <pre className='bg-slate-100 dark:bg-slate-800 p-4 rounded-lg text-sm font-mono overflow-auto max-h-96'>
                {JSON.stringify(
                  {
                    id: signal.id,
                    event: signal.signal_name,
                    signal_type: signal.signal_type,
                    timestamp: signal.timestamp,
                    properties: signal.properties,
                    data: signal.data,
                    person: signal.person,
                    library: signal.library,
                    library_version: signal.library_version,
                    url: signal.url,
                    screen: signal.screen,
                    processed: signal.processed,
                    created_at: signal.created_at,
                    updated_at: signal.updated_at,
                    ...(signal.signal_type === 'agent' && {
                      agent_id: signal.agent_id,
                    }),
                    ...(signal.signal_type === 'feature' && {
                      feature_id: signal.feature_id,
                    }),
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          </div>

          <div className='flex justify-end'>
            <Button variant='outline' onClick={() => setShowRawData(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
